/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Dashboard Hooks - React Query for Super Admin Dashboard
 * ============================================================================
 *
 * Custom hooks for dashboard data fetching using React Query.
 * Falls back to mock data during development when API is unavailable.
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '@/services/superadmin/dashboard.service';
import {
  PlatformStats,
  ActivityItem,
  DashboardSummary,
} from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: () => [...dashboardKeys.all, 'summary'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  activity: (limit?: number) => [...dashboardKeys.all, 'activity', { limit }] as const,
};

// Mock data for development when API is unavailable
const mockDashboardSummary: DashboardSummary = {
  stats: {
    totalOrganizations: 47,
    activeOrganizations: 39,
    trialOrganizations: 8,
    suspendedOrganizations: 2,
    pendingApproval: 5,
    totalUsers: 312,
    monthlyRecurringRevenue: 8450,
    subscriptionCounts: { STARTER: 28, PROFESSIONAL: 14, ENTERPRISE: 5 },
    subscriptionRevenue: { STARTER: 1400, PROFESSIONAL: 2800, ENTERPRISE: 2500 },
  },
  recentActivity: [
    {
      id: '1',
      type: 'ORG_CREATED',
      description: 'Pro Plan',
      entityId: 'org-1',
      entityName: 'New Organization: Conakry Motors',
      createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(), // 2 minutes ago
    },
    {
      id: '2',
      type: 'PAYMENT_RECEIVED',
      description: '$500.00',
      entityId: 'payment-1',
      entityName: 'Payment Received: ACME Corp',
      createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
    },
    {
      id: '3',
      type: 'USER_REGISTERED',
      description: 'Pending approval',
      entityId: 'reg-1',
      entityName: 'Registration Request: Tech Solutions GN',
      createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago
    },
  ],
  attentionItems: [
    {
      type: 'PENDING_REGISTRATION',
      title: '5 Registration Requests',
      description: 'Awaiting your approval',
      count: 5,
      urgency: 'MEDIUM',
    },
    {
      type: 'FAILED_PAYMENT',
      title: '3 Failed Payments',
      description: 'In the last 7 days',
      count: 3,
      urgency: 'HIGH',
    },
    {
      type: 'PENDING_WITHDRAWAL',
      title: '3 Pending Withdrawals',
      description: '$1,250 total requested',
      count: 3,
      urgency: 'LOW',
    },
  ],
};

/**
 * Hook to fetch complete dashboard summary
 * Set useMockData to true to use mock data during development
 */
export function useDashboardSummary(useMockData: boolean = false) {
  return useQuery<DashboardSummary, ApiError>({
    queryKey: dashboardKeys.summary(),
    queryFn: async () => {
      if (useMockData) {
        // Simulate network delay for mock data
        await new Promise(resolve => setTimeout(resolve, 500));
        return mockDashboardSummary;
      }
      return await dashboardService.getDashboardSummary();
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 1, // Retry once on failure
  });
}

/**
 * Hook to fetch platform statistics only
 */
export function useDashboardStats() {
  return useQuery<PlatformStats, ApiError>({
    queryKey: dashboardKeys.stats(),
    queryFn: dashboardService.getStats,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch recent activity
 */
export function useRecentActivity(limit: number = 10) {
  return useQuery<ActivityItem[], ApiError>({
    queryKey: dashboardKeys.activity(limit),
    queryFn: () => dashboardService.getRecentActivity(limit),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch organizations by tier
 */
export function useOrganizationsByTier() {
  return useQuery<Record<string, number>, ApiError>({
    queryKey: [...dashboardKeys.all, 'organizations-by-tier'] as const,
    queryFn: dashboardService.getOrganizationsByTier,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch security metrics
 */
export function useSecurityMetrics() {
  return useQuery<Record<string, unknown>, ApiError>({
    queryKey: [...dashboardKeys.all, 'security'] as const,
    queryFn: dashboardService.getSecurityMetrics,
    staleTime: 30 * 1000,
  });
}
